/**
 * Video utility functions for ProductGallery
 */

const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'];

const VIDEO_MIME_TYPES: Record<string, string> = {
  '.mp4': 'video/mp4',
  '.webm': 'video/webm',
  '.ogg': 'video/ogg',
  '.mov': 'video/quicktime',
  '.avi': 'video/x-msvideo',
  '.mkv': 'video/x-matroska',
};

/**
 * Gets the MIME type for a video URL based on file extension
 * @param url - The video URL
 * @returns The MIME type string, defaults to 'video/mp4' if unknown
 */
export function getVideoMimeType(url: string): string {
  if (!url) return 'video/mp4';
  const lowerUrl = url.toLowerCase();
  const ext = VIDEO_EXTENSIONS.find((e) => lowerUrl.endsWith(e));
  return ext ? VIDEO_MIME_TYPES[ext] : 'video/mp4';
}

/**
 * Checks if URL is a direct video file based on extension
 * @param url - The video URL
 * @returns true if direct video file
 */
export function isDirectVideoFile(url: string): boolean {
  if (!url) return false;
  const lowerUrl = url.toLowerCase();
  return VIDEO_EXTENSIONS.some((ext) => lowerUrl.endsWith(ext));
}

/**
 * Gets the preview image URL for a video.
 * Uses provided preview URL if available, otherwise tries to derive from video URL.
 * @param videoUrl - The video URL
 * @param previewUrl - The preview URL from data (if available)
 * @returns The preview image URL or undefined
 */
export function getVideoPreviewUrl(
  videoUrl: string,
  previewUrl?: string
): string | undefined {
  // Use provided preview URL if available
  if (previewUrl) return previewUrl;

  // Fallback: Derive thumbnail for AEM Assets URLs
  // Pattern: .../urn:aaid:aem:.../play -> .../urn:aaid:aem:.../as/thumbnail.png
  if (videoUrl?.includes('adobeaemcloud.com') && videoUrl.endsWith('/play')) {
    return videoUrl.replace(/\/play$/, '/as/thumbnail.png');
  }

  return undefined;
}

/**
 * Converts YouTube/Vimeo URLs to embeddable URLs.
 * Returns original URL for other video sources.
 * @param url - The original video URL
 * @returns The embed URL
 */
export function getEmbedUrl(url: string): string {
  if (!url) return '';

  // YouTube: various formats
  // https://www.youtube.com/watch?v=VIDEO_ID
  // https://youtu.be/VIDEO_ID
  // https://www.youtube.com/embed/VIDEO_ID
  const youtubeMatch = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/
  );
  if (youtubeMatch) {
    return `https://www.youtube.com/embed/${youtubeMatch[1]}?rel=0`;
  }

  // Vimeo: various formats
  // https://vimeo.com/VIDEO_ID
  // https://player.vimeo.com/video/VIDEO_ID
  const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeoMatch) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  }

  // Return original URL for other sources (AEM, etc.)
  return url;
}
